import React from "react";
import { AlertTriangle } from "lucide-react";

const LOCKED = "Status changes are locked while an undo request is awaiting approval.";

/**
 * What the banner says to this viewer. The server decides who they are
 * (`can_cancel` for the requester, `can_review` for the approver), so the page
 * does no role check of its own. Names go to the two people the request is about;
 * a bystander is told a request is pending and nothing about who is in it.
 * Until the request loads, the lock is still true, so the generic line stands.
 */
const bannerMessage = (request) => {
  if (!request) return LOCKED;

  if (request.can_review) {
    return `${request.requested_by?.name} requested to reopen this candidate. Review it from the status control to approve or reject.`;
  }

  if (request.can_cancel) {
    let decider = `Status changes are locked until ${request.assigned_approver?.name} decides.`;
    if (request.no_eligible_approver) {
      decider = "No approver is available, so status changes stay locked until you cancel it.";
    } else if (request.approver_scope === "ADMIN_POOL") {
      decider = "Status changes are locked until an admin decides.";
    }
    return `You requested to reopen this candidate. ${decider} You can cancel the request from the status control.`;
  }

  return "An undo request to reopen this candidate is awaiting approval. Status changes are locked until it is decided.";
};

/**
 * Why the status control will not move. Shown to everyone who can see the
 * candidate, whether or not they may change a status, because the lock is the
 * server's and a viewer should understand it rather than wonder.
 */
const PendingUndoBanner = ({ request }) => (
  <div
    role="status"
    className="flex shrink-0 items-center gap-2 rounded-lg border border-border-primary bg-background-secondary px-3 py-2 text-[12px] font-medium text-text-primary"
  >
    <AlertTriangle size={14} className="shrink-0 text-warning" aria-hidden="true" />
    <span>{bannerMessage(request)}</span>
  </div>
);

export default PendingUndoBanner;
